import type { CSSProperties } from 'react'
import { useMemo, useState } from 'react'
import { AlertCircle, ArrowRight, Pause, Play, Plus, Radar, Trash2 } from 'lucide-react'

import { CreateTaskModal } from '@/components/calendar/CreateTaskModal'
import type { RecurringTaskProposal } from '@/features/recurring/workflow'
import {
  buildCadenceSummary,
  workflowActivationLabel,
  workflowHealthLabel,
  workflowKindAccent,
  workflowKindLabel,
  workflowReadiness,
} from '@/features/recurring/workflow'
import { useAuth } from '@/hooks/useAuth'
import { categoryColor, useRecurringTasks } from '@/hooks/useRecurringTasks'
import type { RecurringTask, TaskExecution } from '@/hooks/useRecurringTasks'

function executionLabel(execution: TaskExecution | null): string {
  if (!execution) return 'Ingen kjøringer'
  if (execution.status === 'running') return 'Kjører nå'
  if (execution.status === 'pending') return 'Venter'
  if (execution.status === 'success') return 'Fullført'
  if (execution.status === 'failed') return 'Feilet'
  return 'Hoppet over'
}

function executionColor(execution: TaskExecution | null): string {
  if (!execution) return '#5A5A5A'
  if (execution.status === 'failed') return '#EF4444'
  if (execution.status === 'success') return '#4ADE80'
  if (execution.status === 'running' || execution.status === 'pending') return '#F59E0B'
  return '#5A5A5A'
}

export function ScheduledTasksContent() {
  const { user } = useAuth()
  const { tasks, executionsByTask, loading, createTask, toggleActive, deleteTask } = useRecurringTasks(user?.id ?? null)
  const [showCreate, setShowCreate] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const latestExecution = (taskId: string): TaskExecution | null => executionsByTask[taskId]?.[0] ?? null

  const grouped = useMemo(() => {
    const monitorLike = tasks.filter(task => task.workflowSpec?.kind === 'monitor')
    const reports = tasks.filter(task => task.workflowSpec?.kind === 'report')
    const briefings = tasks.filter(task => task.workflowSpec?.kind === 'reminder')
    const everythingElse = tasks.filter(task => !task.workflowSpec?.kind || task.workflowSpec.kind === 'sync')
    return [
      { title: 'Monitorer', subtitle: 'Fast overvåkning med signaler og alerts.', items: monitorLike },
      { title: 'Rapporter', subtitle: 'Gjennomgående rapportflyter og rollups.', items: reports },
      { title: 'Briefinger og annet', subtitle: 'Faste briefinger, syncer og øvrige workflows.', items: [...briefings, ...everythingElse] },
    ].filter(group => group.items.length > 0)
  }, [tasks])

  const handleCreate = async (proposal: RecurringTaskProposal) => {
    await createTask(proposal, 'manual', false)
    setShowCreate(false)
  }

  const handleDelete = async (task: RecurringTask) => {
    setDeletingId(task.id)
    await deleteTask(task.id)
    setDeletingId(null)
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 className="settings-title" style={{ margin: '0 0 6px' }}>Recurring workflows</h2>
          <p style={{ margin: 0, fontSize: 13, color: '#7A7A7A', lineHeight: 1.55 }}>
            Administrer monitorer, faste rapporter og briefinger som levende agentiske flyter.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 14px',
            borderRadius: 8,
            background: '#F0F0F0',
            border: 'none',
            color: '#111',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <Plus size={14} />
          Ny workflow
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div style={{ width: 20, height: 20, border: '2px solid #333', borderTopColor: '#1A93FE', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : tasks.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 0',
          gap: 14,
        }}>
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: '#4B5563',
            border: '1px solid #2A2A2A',
            background: '#161616',
          }}>
            <Radar size={24} />
          </div>
          <p style={{ fontSize: 14, color: '#5A5A5A', textAlign: 'center', margin: 0 }}>
            Du har ingen recurring workflows enda
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 18 }}>
          {grouped.map(group => (
            <section key={group.title}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#E5E5E5', marginBottom: 4 }}>{group.title}</div>
                <div style={{ fontSize: 12, color: '#6B7280' }}>{group.subtitle}</div>
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                {group.items.map(task => {
                  const color = task.color || categoryColor(task.category)
                  const execution = latestExecution(task.id)
                  const kind = task.workflowSpec?.kind
                  const kindAccent = workflowKindAccent(kind)
                  const triggerLabel = task.workflowSpec?.triggerLabel || buildCadenceSummary(task)
                  const alertCount = task.workflowSpec?.alertConditions?.length ?? 0
                  const destinationCount = task.workflowSpec?.destinations?.length ?? 0
                  const readiness = workflowReadiness({
                    title: task.title,
                    description: task.description,
                    agentPrompt: task.agentPrompt,
                    connectorId: task.connectorId,
                    connectorName: task.connectorName,
                    workflowSpec: task.workflowSpec,
                  })
                  const activationLabel = workflowActivationLabel(task.workflowSpec?.activationMode)

                  return (
                    <div
                      key={task.id}
                      style={{
                        padding: '14px 16px',
                        borderRadius: 14,
                        background: '#1A1A1A',
                        border: `1px solid ${kind ? kindAccent.border : '#2A2A2A'}`,
                        opacity: task.isActive ? 1 : 0.6,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                            <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#E5E5E5' }}>{task.title}</span>
                            {kind && (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                padding: '2px 8px',
                                borderRadius: 999,
                                background: kindAccent.background,
                                border: `1px solid ${kindAccent.border}`,
                                color: kindAccent.text,
                                fontSize: 10,
                                fontWeight: 700,
                              }}>
                                {workflowKindLabel(kind)}
                              </span>
                            )}
                            <span style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '2px 7px',
                              borderRadius: 999,
                              background: `${color}22`,
                              color,
                            }}>
                              {task.category}
                            </span>
                          </div>

                          <div style={{ fontSize: 12, color: '#CFCFCF', lineHeight: 1.6, marginBottom: 8 }}>
                            {task.workflowSpec?.objective || task.description || 'Ingen beskrivelse lagt inn ennå.'}
                          </div>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                            <MetaPill label="Trigger" value={triggerLabel} />
                            <MetaPill label="Alerts" value={alertCount > 0 ? `${alertCount} regler` : 'Ingen'} />
                            <MetaPill label="Leveranse" value={destinationCount > 0 ? `${destinationCount} kanaler` : 'Ikke satt'} />
                            <MetaPill label="Aktivering" value={activationLabel} />
                            <MetaPill label="Status" value={task.isActive ? 'Aktiv' : 'Pauset'} />
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: executionColor(execution) }}>
                              {execution?.status === 'failed' ? <AlertCircle size={11} /> : <ArrowRight size={11} />}
                              <span>{executionLabel(execution)}</span>
                            </div>
                            <div style={{ fontSize: 11, color: readiness.isReady ? '#86EFAC' : '#FCD34D' }}>
                              {workflowHealthLabel(readiness.score)} · {readiness.score}/100
                            </div>
                          </div>

                          {(readiness.blockers.length > 0 || readiness.warnings.length > 0) && (
                            <div style={{ marginTop: 8, fontSize: 11, lineHeight: 1.5, color: readiness.blockers.length > 0 ? '#FCA5A5' : '#A3A3A3' }}>
                              {readiness.blockers[0] || readiness.warnings[0]}
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button
                            onClick={() => toggleActive(task.id, !task.isActive)}
                            title={task.isActive ? 'Sett på pause' : 'Aktiver'}
                            style={iconButtonStyle(task.isActive ? '#1A93FE' : '#5A5A5A')}
                          >
                            {task.isActive ? <Pause size={13} /> : <Play size={13} />}
                          </button>
                          <button
                            onClick={() => void handleDelete(task)}
                            disabled={deletingId === task.id}
                            title="Slett workflow"
                            style={iconButtonStyle('#5A5A5A')}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateTaskModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />
      )}
    </div>
  )
}

function MetaPill({ label, value }: { label: string; value: string }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 8px',
      borderRadius: 999,
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.06)',
      fontSize: 11,
      color: '#9CA3AF',
    }}>
      <strong style={{ color: '#D1D5DB', fontWeight: 600 }}>{label}</strong>
      <span>{value}</span>
    </span>
  )
}

function iconButtonStyle(color: string): CSSProperties {
  return {
    width: 30,
    height: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color,
    cursor: 'pointer',
  }
}
